import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import API from "../../../Network/API";
import isTenantUser from "../../../CustomHook/tenantUser";

const isCanceled = (err) =>
  err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.message === "canceled";

export default function useReportForm(dispatch) {
  const { user: profileUser } = useSelector((state) => state.profileReducer);
  const { user: authUser } = useSelector((state) => state.authReducer);
  const user = profileUser?.id ? profileUser : authUser;
  const tenantUser = isTenantUser(user);

  const [form, setForm] = useState({
    report_type: null,
    cluster: "",
    zone: "",
    site: "",
    tenant: tenantUser ? user.tenant_id : "",
    duration_type: 1,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    rms_alarm: [],
    acs_alarm: [],
  });

  const [options, setOptions] = useState({
    clusters: [],
    zones: [],
    sites: [],
    report_types: [],
    rms_alarms: [],
    tenants: [],
    acs_alarms: [],
  });

  const [loading, setLoading] = useState(false);
  const siteSearchTimer = useRef(null);
  const siteSearchAbort = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    API.get("/v1/oauth/report_index", { signal: controller.signal })
      .then((res) => {
        const data = res.data.data;
        setOptions((prev) => ({
          clusters: data.clusters,
          report_types: data.report_types,
          rms_alarms: data.rms_alarms,
          tenants: data.tenants,
          acs_alarms: data.lock_alarms,
          zones: prev.zones,
          sites: prev.sites,
        }));
        dispatch({ type: "REPORT_TYPE_FETCH_SUCCESS", payload: data });
        dispatch({ type: "RMS_ALARM_MASTER_FETCH_SUCCESS", payload: data.rms_alarms });
      })
      .catch((err) => {
        if (isCanceled(err)) return;
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => {
      controller.abort();
      if (siteSearchTimer.current) clearTimeout(siteSearchTimer.current);
      if (siteSearchAbort.current) siteSearchAbort.current.abort();
    };
  }, [dispatch]);

  useEffect(() => {
    if (tenantUser && user?.tenant_id) {
      setForm((prev) => ({ ...prev, tenant: user.tenant_id }));
    }
  }, [tenantUser, user?.tenant_id]);

  const handleClusterChange = (selected) => {
    setForm((prev) => ({ ...prev, cluster: selected?.value || "", zone: "", site: "" }));
    if (!selected) return setOptions((prev) => ({ ...prev, zones: [], sites: [] }));

    API.get(`/v1/oauth/ajax_cluster_zone/${selected.value}`)
      .then((res) => setOptions((prev) => ({ ...prev, zones: res.data.data.zone })))
      .catch((err) => {
        if (isCanceled(err)) return;
      });
  };

  const handleZoneChange = (selected) => {
    setForm((prev) => ({ ...prev, zone: selected?.value || "", site: "" }));
    if (!selected) return setOptions((prev) => ({ ...prev, sites: [] }));

    API.get(`/v1/oauth/ajax_sites?zone_id=${selected.value}`)
      .then((res) => setOptions((prev) => ({ ...prev, sites: res.data.data.sites })))
      .catch((err) => {
        if (isCanceled(err)) return;
      });
  };

  const handleSiteChange = (selected) => {
    setForm((prev) => ({ ...prev, site: selected?.value || "" }));
    if (!selected) return;
    setOptions((prev) => {
      const exists = prev.sites.some((s) => String(s.value) === String(selected.value));
      return exists ? prev : { ...prev, sites: [selected, ...prev.sites] };
    });
  };

  const handleSiteSearch = (inputValue, actionMeta) => {
    if (actionMeta?.action && actionMeta.action !== "input-change") {
      return inputValue;
    }

    if (siteSearchTimer.current) clearTimeout(siteSearchTimer.current);
    const q = String(inputValue || "").trim();
    if (!q) return inputValue;

    siteSearchTimer.current = setTimeout(() => {
      if (siteSearchAbort.current) siteSearchAbort.current.abort();
      const controller = new AbortController();
      siteSearchAbort.current = controller;

      const params = new URLSearchParams({ q });
      if (formRef.current.zone) params.set("zone_id", formRef.current.zone);

      API.get(`/v1/oauth/ajax_sites?${params.toString()}`, { signal: controller.signal })
        .then((res) => {
          const sites = res.data?.data?.sites ?? [];
          setOptions((prev) => {
            const selectedVal = formRef.current.site;
            const selected =
              prev.sites.find((s) => String(s.value) === String(selectedVal))
              || (selectedVal ? { value: selectedVal, label: selectedVal } : null);
            if (selected && !sites.some((s) => String(s.value) === String(selected.value))) {
              return { ...prev, sites: [selected, ...sites] };
            }
            return { ...prev, sites };
          });
        })
        .catch((err) => {
          if (isCanceled(err)) return;
        });
    }, 200);

    return inputValue;
  };

  const handleAlarmsChange = (field, selected) => {
    setForm((prev) => ({ ...prev, [field]: selected || [] }));
  };

  const resetForm = () => {
    setForm({
      report_type: null,
      cluster: "",
      zone: "",
      site: "",
      tenant: tenantUser ? user?.tenant_id : "",
      duration_type: 1,
      start_date: new Date().toISOString().slice(0, 10),
      end_date: "",
      rms_alarm: [],
      acs_alarm: [],
    });
  };

  return {
    form,
    setForm,
    options,
    loading,
    handleClusterChange,
    handleZoneChange,
    handleSiteChange,
    handleSiteSearch,
    handleAlarmsChange,
    resetForm,
  };
}
